import React, { useMemo } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Button,
} from "@mui/material";
import { styled } from "@mui/system";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

// Custom styled components
const CustomTableCell = styled(TableCell)(({ columnWidth }) => ({
  backgroundColor: "#424752",
  fontSize: "13px",
  textAlign: "center",
  color: "white",
  border: "1px solid #e0e0e0",
  padding: "8px 12px",
  minWidth: columnWidth || 100,
  maxWidth: columnWidth || 100,
  whiteSpace: "normal",
  wordWrap: "break-word",
  overflow: "hidden",
  textOverflow: "ellipsis",
}));

const StickyTableCell = styled(CustomTableCell)(({ left, isLastSticky }) => ({
  position: "sticky",
  left: left || 0,
  zIndex: 3,
  boxShadow: isLastSticky ? "2px 0 4px rgba(0,0,0,0.2)" : "none",
}));

const CustomTableRow = styled(TableRow)(({ index }) => ({
  backgroundColor: index % 2 === 0 ? "#FFFFFF" : "#F7F7F7",
}));

const BodyTableCell = styled(TableCell)(({ columnWidth }) => ({
  border: "0px solid #e0e0e0",
  padding: "8px 12px",
  textAlign: "center",
  fontSize: "12px",
  minWidth: columnWidth || 100,
  maxWidth: columnWidth || 100,
  whiteSpace: "normal",
  wordWrap: "break-word",
  overflow: "hidden",
  textOverflow: "ellipsis",
}));

const StickyBodyTableCell = styled(BodyTableCell)(({ left, isLastSticky, rowIndex }) => ({
  position: "sticky",
  left: left || 0,
  zIndex: 1,
  backgroundColor: rowIndex % 2 === 0 ? "#FFFFFF" : "#F7F7F7",
  boxShadow: isLastSticky ? "2px 0 4px rgba(0,0,0,0.2)" : "none",
}));

// Helper function to format date
const formatDate = (date) => {
  if (!date) return "";
  try {
    const d = new Date(date);
    return d.toISOString().split("T")[0];
  } catch {
    return "";
  }
};

// Helper function to estimate text width with a cap
const estimateTextWidth = (text) => {
  if (!text) return 100;
  const charWidth = 8;
  const padding = 24;
  const estimatedWidth = text.length * charWidth + padding;
  return Math.min(estimatedWidth, 8 * window.innerWidth / 100);
};

// Helper function to generate all dates in range
const getDateRange = (startDate, endDate) => {
  const dates = [];
  if (!startDate || !endDate) return dates;
  const start = new Date(startDate);
  const end = new Date(endDate);
  if (isNaN(start.getTime()) || isNaN(end.getTime())) return [];
  for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
    dates.push(formatDate(new Date(d)));
  }
  return dates;
};

// Human-readable labels for parameters
const parameterLabels = {
  plate1: "Plate 1",
  plate2: "Plate 2",
  plate3: "Plate 3",
  plate4: "Plate 4",
  plate5: "Plate 5",
  setTemperature: "Set Temperature (°C)",
  weldingTime: "Welding Time",
  remarks: "Remarks",
};

const StringerTable = ({ data, startDate, endDate, shifts, subForm }) => {
  // Debug: Log received props
  console.log("StringerTable props:", { data, startDate, endDate, shifts, subForm });

  // Get all dates in the range
  const dates = getDateRange(startDate, endDate);

  // Use provided shifts, fallback to data if shifts prop is empty
  const effectiveShifts = shifts.length > 0 ? shifts : [
    ...new Set(data.map((item) => item?.inspectionShift).filter(Boolean))
  ].sort();
  console.log("Effective Shifts:", effectiveShifts);

  // Get unique stringers per date and shift
  const stringersByDateShift = useMemo(() => {
    const map = {};
    data.forEach((item) => {
      const date = formatDate(item.inspectionDate);
      const shift = item.inspectionShift;
      if (!date || !shift) return;
      const dateShiftKey = `${date}_${shift}`;
      if (!map[dateShiftKey]) {
        map[dateShiftKey] = new Set();
      }
      Object.values(item.form_data || {})
        .filter((stringerData) => stringerData && stringerData.stringer)
        .forEach((stringerData) => {
          map[dateShiftKey].add(stringerData.stringer);
        });
    });
    // Convert Sets to sorted arrays
    Object.keys(map).forEach((key) => {
      map[key] = [...map[key]].sort();
    });
    return map;
  }, [data]);
  console.log("Stringers by Date and Shift:", stringersByDateShift);

  // Calculate column widths based on content
  const columnWidths = useMemo(() => {
    const widths = {
      section: estimateTextWidth("Section A"),
      parameter: estimateTextWidth("Set Temperature (°C)"),
      parameter: estimateTextWidth("Welding Time"),
      value: 100, // Default width for value columns
    };

    data.forEach((item) => {
      Object.entries(item.form_data || {}).forEach(([_, stringerData]) => {
        if (!stringerData || !stringerData.stringer) return;
        widths[stringerData.stringer] = Math.min(
          Math.max(widths[stringerData.stringer] || 100, estimateTextWidth(stringerData.stringer)),
          8 * window.innerWidth / 100
        );
        ["sectionA", "sectionB"].forEach((sectionKey) => {
          const sectionData = stringerData[sectionKey] || {};
          widths.section = Math.min(
            Math.max(widths.section, estimateTextWidth(sectionKey === "sectionA" ? "Section A" : "Section B")),
            8 * window.innerWidth / 100
          );
          Object.entries(sectionData.heatingPlate || {}).forEach(([plate, value]) => {
            widths.parameter = Math.min(
              Math.max(widths.parameter, estimateTextWidth(parameterLabels[plate] || plate)),
              8 * window.innerWidth / 100
            );
            widths.value = Math.min(
              Math.max(widths.value, estimateTextWidth(String(value))),
              8 * window.innerWidth / 100
            );
          });
          ["weldingTime", "remarks", "setTemperature"].forEach((param) => {
            widths.parameter = Math.min(
              Math.max(widths.parameter, estimateTextWidth(parameterLabels[param] || param)),
              8 * window.innerWidth / 100
            );
            widths.value = Math.min(
              Math.max(widths.value, estimateTextWidth(String(sectionData[param]))),
              8 * window.innerWidth / 100
            );
          });
        });
      });
    });

    return widths;
  }, [data]);

  // Calculate cumulative left positions for sticky columns
  const getLeftPosition = (index) => {
    if (index === 0) return 0;
    if (index === 1) return columnWidths.section;
    return columnWidths.section + columnWidths.parameter;
  };

  // Prepare table rows
  const tableRows = useMemo(() => {
    const parameters = [
      "setTemperature",
      "weldingTime",
      "plate1",
      "plate2",
      "plate3",
      "plate4",
      "plate5",
      "remarks",
    ];
    const rows = [];
    ["sectionA", "sectionB"].forEach((sectionKey) => {
      const sectionLabel = sectionKey === "sectionA" ? "Section A" : "Section B";
      parameters.forEach((param) => {
        rows.push({
          section: sectionLabel,
          parameter: parameterLabels[param] || param,
          paramKey: param,
          sectionKey,
        });
      });
    });
    return rows;
  }, []);

  // Prepare table data
  const tableData = useMemo(() => {
    const dataMap = {};
    data.forEach((item) => {
      const dateShiftKey = `${formatDate(item.inspectionDate)}_${item.inspectionShift}`;
      Object.entries(item.form_data || {}).forEach(([_, stringerData]) => {
        if (!stringerData || !stringerData.stringer) return;
        const stringer = stringerData.stringer;
        ["sectionA", "sectionB"].forEach((sectionKey) => {
          const sectionData = stringerData[sectionKey] || {};
          const keyPrefix = `${sectionKey}_${stringer}_${dateShiftKey}`;
          ["weldingTime", "remarks", "setTemperature"].forEach((param) => {
            dataMap[`${keyPrefix}_${param}`] = sectionData[param] || "N/A";
          });
          Object.entries(sectionData.heatingPlate || {}).forEach(([plate, value]) => {
            dataMap[`${keyPrefix}_${plate}`] = value || "N/A";
          });
        });
      });
    });
    return dataMap;
  }, [data]);

  const exportToExcel = () => {
    const wb = XLSX.utils.book_new();
    const merges = [];

    // Row 1: Date + Shift (merged across all stringers in that shift)
    const row1 = ["Section", "Parameter"];
    let colIdx = 2;

    dates.forEach((date) => {
      effectiveShifts.forEach((shift) => {
        const key = `${date}_${shift}`;
        const stringers = stringersByDateShift[key] || [];
        const totalCols = stringers.length * 2; // 2 sections per stringer
        if (totalCols > 0) {
          row1.push(`${date} ${shift}`);
          if (totalCols > 1) {
            merges.push({ s: { r: 0, c: colIdx }, e: { r: 0, c: colIdx + totalCols - 1 } });
          }
          colIdx += totalCols;
        }
      });
    });

    // Row 2: Stringer names (merged across 2 sections)
    const row2 = ["", ""];
    colIdx = 2;
    dates.forEach((date) => {
      effectiveShifts.forEach((shift) => {
        const key = `${date}_${shift}`;
        const stringers = stringersByDateShift[key] || [];
        stringers.forEach((stringer) => {
          row2.push(stringer);
          row2.push(stringer);
          merges.push({ s: { r: 1, c: colIdx }, e: { r: 1, c: colIdx + 1 } });
          colIdx += 2;
        });
      });
    });

    // Row 3: Section A / Section B
    const row3 = ["", ""];
    dates.forEach((date) => {
      effectiveShifts.forEach((shift) => {
        const key = `${date}_${shift}`;
        const stringers = stringersByDateShift[key] || [];
        stringers.forEach(() => {
          row3.push("Section A");
          row3.push("Section B");
        });
      });
    });

    // Data rows
    const dataRows = tableRows.map((row) => {
      const r = [row.section, row.parameter];
      dates.forEach((date) => {
        effectiveShifts.forEach((shift) => {
          const key = `${date}_${shift}`;
          const stringers = stringersByDateShift[key] || [];
          stringers.forEach((stringer) => {
            const prefix = `${row.sectionKey}_${stringer}_${key}`;
            const value = tableData[`${prefix}_${row.paramKey}`] || "N/A";
            r.push(value);
            // Second column is always the same for both sections? No — we already handle per section
            // Actually, since each stringer has 2 columns (A and B), we already pushed both above
          });
        });
      });
      return r;
    });

    const sheetData = [row1, row2, row3, ...dataRows];
    const ws = XLSX.utils.aoa_to_sheet(sheetData);

    // Apply all merges
    if (merges.length > 0) ws["!merges"] = merges;

    // Column widths
    ws["!cols"] = [
      { wch: 15 }, // Section
      { wch: 28 }, // Parameter
      ...Array(sheetData[0].length - 2).fill({ wch: 18 }),
    ];

    XLSX.utils.book_append_sheet(wb, ws, "Stringer Parameters");

    const fileName = `Stringer_Machine_Parameters_${formatDate(startDate || new Date())}_to_${formatDate(endDate || new Date())}.xlsx`;
    const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });

    saveAs(new Blob([excelBuffer], { type: "application/octet-stream" }), fileName);
  };

  return (
    <div style={{ margin: "20px", overflowX: "auto", maxWidth: "100vw" }}>
      <Typography variant="h6" gutterBottom>
        Stringer Inspection Data
      </Typography>

      <Button
        variant="contained"
        color="success"
        onClick={exportToExcel}
        sx={{ mb: 2 }}
        disabled={tableRows.length === 0}
      >
        Download as Excel
      </Button>
      
      <TableContainer component={Paper} sx={{ maxWidth: "100%", overflowX: "auto" }}>
        <Table sx={{ tableLayout: "fixed", width: "max-content" }}>
          <TableHead>
            <TableRow>
              <StickyTableCell rowSpan={3} columnWidth={columnWidths.section} left={getLeftPosition(0)} isLastSticky={false}>
                Section
              </StickyTableCell>
              <StickyTableCell rowSpan={3} columnWidth={columnWidths.parameter} left={getLeftPosition(1)} isLastSticky={true}>
                Parameter
              </StickyTableCell>
              {dates.map((date) => {
                const colSpan = effectiveShifts.reduce((sum, shift) => {
                  const stringers = stringersByDateShift[`${date}_${shift}`] || [];
                  return sum + stringers.length;
                }, 0);
                return (
                  <CustomTableCell key={date} colSpan={colSpan} columnWidth={columnWidths.value * colSpan}>
                    {date}
                  </CustomTableCell>
                );
              })}
            </TableRow>
            <TableRow>
              {dates.flatMap((date) =>
                effectiveShifts.map((shift) => {
                  const stringers = stringersByDateShift[`${date}_${shift}`] || [];
                  return (
                    <CustomTableCell key={`${date}_${shift}`} colSpan={stringers.length} columnWidth={columnWidths.value * stringers.length}>
                      Shift {shift}
                    </CustomTableCell>
                  );
                })
              )}
            </TableRow>
            <TableRow>
              {dates.flatMap((date) =>
                effectiveShifts.flatMap((shift) => {
                  const stringers = stringersByDateShift[`${date}_${shift}`] || [];
                  return stringers.map((stringer) => (
                    <CustomTableCell key={`${date}_${shift}_${stringer}`} columnWidth={columnWidths.value}>
                      {stringer}
                    </CustomTableCell>
                  ));
                })
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {tableRows.length === 0 ? (
              <CustomTableRow index={0}>
                <StickyBodyTableCell
                  colSpan={2 + dates.reduce((sum, date) => 
                    sum + effectiveShifts.reduce((s, shift) => 
                      s + (stringersByDateShift[`${date}_${shift}`]?.length || 0), 0
                    ), 0)}
                  align="center"
                  rowIndex={0}
                  columnWidth={columnWidths.section + columnWidths.parameter}
                  left={getLeftPosition(0)}
                  isLastSticky={true}
                >
                  No data available
                </StickyBodyTableCell>
              </CustomTableRow>
            ) : (
              tableRows.map((row, index) => (
                <CustomTableRow key={`${row.section}_${row.parameter}_${index}`} index={index}>
                  <StickyBodyTableCell
                    rowIndex={index}
                    columnWidth={columnWidths.section}
                    left={getLeftPosition(0)}
                    isLastSticky={false}
                  >
                    {row.section}
                  </StickyBodyTableCell>
                  <StickyBodyTableCell
                    rowIndex={index}
                    columnWidth={columnWidths.parameter}
                    left={getLeftPosition(1)}
                    isLastSticky={true}
                  >
                    {row.parameter}
                  </StickyBodyTableCell>
                  {dates.flatMap((date) =>
                    effectiveShifts.flatMap((shift) => {
                      const stringers = stringersByDateShift[`${date}_${shift}`] || [];
                      return stringers.map((stringer) => (
                        <BodyTableCell
                          key={`${date}_${shift}_${stringer}_${row.section}_${row.paramKey}`}
                          columnWidth={columnWidths.value}
                        >
                          {tableData[`${row.sectionKey}_${stringer}_${date}_${shift}_${row.paramKey}`] || "N/A"}
                        </BodyTableCell>
                      ));
                    })
                  )}
                </CustomTableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
};

export default StringerTable;